import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Layers, Award, ClipboardList, IndianRupee, Store, TrendingUp } from "lucide-react";
import { scrollToId, waLink, PHONE_TEL, PHONE_DISPLAY } from "../../lib/contact";

const STATS = [
  { value: "45 Days", label: "Structured Program" },
  { value: "5 Models", label: "Online Businesses" },
  { value: "Practical", label: "Build Your Project" },
  { value: "₹14,999", label: "Program Fee" },
];

const CHIPS = [
  { icon: Store, label: "Offline Classroom Training" },
  { icon: ClipboardList, label: "Practical Assignments" },
  { icon: Award, label: "Certificate" },
];

const MaskedLine = ({ text, accent, index }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block font-display font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-[3.6rem] ${
        accent ? "text-gradient" : "text-white"
      }`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay: 0.35 + index * 0.13, ease: [0.22, 1, 0.36, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const FloatCard = ({ className, icon: Icon, label, value, tone, animClass, testid }) => (
  <div
    data-testid={testid}
    className={`absolute ${className} ${animClass} bg-navy-800/90 backdrop-blur-md border border-white/10 rounded-xl px-4 py-3 shadow-2xl shadow-black/40 flex items-center gap-3`}
  >
    <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone}`}>
      <Icon size={15} className="text-white" />
    </span>
    <span>
      <span className="block text-[10px] uppercase tracking-widest text-slate-400">{label}</span>
      <span className="block font-display font-bold text-white text-sm">{value}</span>
    </span>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative bg-navy overflow-hidden pt-[72px]">
      <div className="absolute inset-0 bg-grid-navy" />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-electric/20 blur-[140px]" />
      <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-cyanx/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="inline-flex items-center gap-2 border border-cyanx/30 bg-cyanx/5 rounded-full px-4 py-1.5"
              data-testid="hero-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-cyanx animate-pulse" />
              <span className="text-[11px] font-bold tracking-[0.2em] text-cyanx uppercase">45-Day Practical Online Business Program · Rampur</span>
            </motion.div>

            <h1 data-testid="hero-headline" className="mt-7">
              <MaskedLine text="Learn Online Business." index={0} />
              <MaskedLine text="Build Real Skills." accent index={1} />
            </h1>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }}>
              <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl" data-testid="hero-subtext">
                Explore five online business models and learn how products, websites, marketplaces, advertising, content, sales and business operations work together.
              </p>
              <p className="mt-3 text-sm sm:text-base text-slate-500 leading-relaxed max-w-xl">
                Choose one business model and build your own practical project under expert guidance.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05 }}
              className="mt-6 flex flex-wrap gap-2.5"
              data-testid="hero-chips"
            >
              {CHIPS.map((c) => (
                <span key={c.label} className="inline-flex items-center gap-2 text-xs font-semibold text-slate-300 border border-white/15 rounded-full px-4 py-2">
                  <c.icon size={13} className="text-cyanx" />
                  {c.label}
                </span>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollToId("contact")}
                className="group inline-flex items-center gap-2 bg-electric hover:bg-electric-dark text-white font-semibold text-sm rounded-full px-7 py-4 transition-all hover:shadow-xl hover:shadow-electric/40 hover:-translate-y-0.5"
              >
                Book a Free Counselling Session
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                data-testid="hero-cta-call"
                href={waLink()}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-cyanx/60 text-slate-200 hover:text-white font-semibold text-sm rounded-full px-7 py-4 transition-colors"
              >
                <Phone size={15} />
                Call / WhatsApp: {PHONE_DISPLAY}
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
                <img
                  src="/assets/hero-dashboard.png"
                  alt="Online business analytics dashboard on a laptop at LaunchX Academy Rampur"
                  className="w-full h-[300px] sm:h-[400px] lg:h-[440px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
              </div>
              <FloatCard testid="hero-float-models" className="-top-5 -left-3 sm:-left-8" icon={Layers} label="Business Models" value="5 in 1 Program" tone="bg-electric" animClass="float-slow" />
              <FloatCard testid="hero-float-profit" className="top-1/3 -right-3 sm:-right-6" icon={TrendingUp} label="Learn" value="Profit, Not Just Revenue" tone="bg-cyanx/80" animClass="float-slower" />
              <FloatCard testid="hero-float-fee" className="-bottom-5 left-8" icon={IndianRupee} label="Program Fee" value="₹14,999" tone="bg-navy-600" animClass="float-slow" />
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.35 }}
          className="mt-16 sm:mt-20 border-t border-white/10 pt-8 grid grid-cols-2 lg:grid-cols-4 gap-6"
          data-testid="hero-trust-bar"
        >
          {STATS.map((s) => (
            <div key={s.value} data-testid={`trust-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <p className="font-display font-extrabold text-2xl sm:text-3xl text-white">{s.value}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
